import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Download, Printer, IdCard } from 'lucide-react';

import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { getCarnetEmpleado } from '@/services/api/empleados.api';
import type { CarnetEmpleado } from '@/types/dominio';
import { CarnetSvg, ANCHO, ALTO } from './CarnetSvg';

/** Factor de rasterizado del PNG: 2× sobre 300 ppp ≈ 600 ppp de impresión. */
const ESCALA_PNG = 2;

interface Props {
  empleadoId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function nombreArchivo(carnet: CarnetEmpleado): string {
  const slug = carnet.nombres
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  return `carnet-${slug || 'colaborador'}-${carnet.documento}.png`;
}

export function CarnetEmpleadoDialog({ empleadoId, open, onOpenChange }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [exportando, setExportando] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['empleado-carnet', empleadoId],
    queryFn: () => getCarnetEmpleado(empleadoId as string),
    enabled: open && !!empleadoId,
    // El QR es la credencial de portería: no la dejamos cacheada de más.
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  /** Serializa el SVG del DOM a un data-URL listo para <img> o para imprimir. */
  function serializarSvg(): { xml: string; url: string } | null {
    if (!svgRef.current) return null;
    const xml = new XMLSerializer().serializeToString(svgRef.current);
    return { xml, url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}` };
  }

  async function descargarPng() {
    const svg = serializarSvg();
    if (!svg || !data) return;

    setExportando(true);
    try {
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('No se pudo rasterizar el carnet'));
        img.src = svg.url;
      });

      const canvas = document.createElement('canvas');
      canvas.width = ANCHO * ESCALA_PNG;
      canvas.height = ALTO * ESCALA_PNG;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('El navegador no soporta canvas 2D');

      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('No se pudo generar el PNG');

      const enlace = document.createElement('a');
      enlace.href = URL.createObjectURL(blob);
      enlace.download = nombreArchivo(data);
      enlace.click();
      URL.revokeObjectURL(enlace.href);

      toast.success('Carnet descargado');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'No se pudo descargar el carnet');
    } finally {
      setExportando(false);
    }
  }

  /**
   * Imprime desde un iframe oculto en lugar de una ventana emergente: no lo
   * bloquea el navegador y no arrastra los estilos del panel.
   */
  function imprimir() {
    const svg = serializarSvg();
    if (!svg || !data) return;

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument;
    if (!doc) {
      iframe.remove();
      toast.error('No se pudo preparar la impresión');
      return;
    }

    doc.open();
    doc.write(
      '<!doctype html><html><head><meta charset="utf-8">'
      + `<title>${nombreArchivo(data).replace(/\.png$/, '')}</title>`
      + '<style>@page{size:54mm 85.6mm;margin:0}'
      + 'html,body{margin:0;padding:0}'
      + 'svg{width:54mm;height:85.6mm;display:block}</style>'
      + `</head><body>${svg.xml}</body></html>`,
    );
    doc.close();

    // Las imágenes son data-URL, pero el layout todavía necesita un tick.
    iframe.contentWindow?.addEventListener('load', () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => iframe.remove(), 1000);
    });
  }

  const mensajeError = error
    ? 'No se pudo generar el carnet. Verifica que el colaborador esté activo.'
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IdCard className="h-5 w-5 text-primary-600" />
            Carnet de acceso
          </DialogTitle>
          <DialogDescription>
            Formato CR80 (54 × 85,6 mm). El QR es el mismo que valida la portería.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <Skeleton className="aspect-[638/1011] w-full rounded-xl" />
        ) : mensajeError || !data ? (
          <p className="rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground">
            {mensajeError ?? 'Sin datos del carnet'}
          </p>
        ) : (
          <div className="rounded-xl border bg-slate-100 p-4">
            <div className="mx-auto max-w-[290px] overflow-hidden rounded-xl shadow-lg">
              <CarnetSvg carnet={data} svgRef={svgRef} className="h-auto w-full" />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={imprimir} disabled={!data || exportando}>
            <Printer className="h-4 w-4" />
            Imprimir
          </Button>
          <Button onClick={descargarPng} disabled={!data || exportando}>
            <Download className="h-4 w-4" />
            {exportando ? 'Generando…' : 'Descargar PNG'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
